import {recaptchaV3SiteKey} from "../../client/PlaygroundSettings";

declare global {
  interface Window {
    grecaptcha?: {
      ready(cb: () => void): void;
      execute(siteKey: string, options: {action: string}): Promise<string>;
    };
  }
}

let scriptLoaded = false;

function loadScript(): void {
  if (scriptLoaded) {
    return;
  }
  scriptLoaded = true;

  const script = document.createElement("script");
  script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaV3SiteKey}`;
  script.async = true;
  document.head.appendChild(script);
}

function waitForGrecaptcha(timeoutMs: number): Promise<void> {
  if (window.grecaptcha) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const started = Date.now();
    const interval = setInterval(() => {
      if (window.grecaptcha) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - started > timeoutMs) {
        clearInterval(interval);
        reject(new Error("reCAPTCHA script failed to load"));
      }
    }, 200);
  });
}

/**
 * Loads the reCAPTCHA v3 script (once) and executes it for the given action.
 *
 * Manual step: app WebView origins (local-app.playgroundsessions.com and
 * production app hosts) must be added to the reCAPTCHA key's allowed domains
 * in the Google reCAPTCHA console.  Missing domains surface as
 * `recaptcha_required` / `recaptcha_failed` from the API.
 */
export const recaptchaV3 = {
  async execute(action: string): Promise<string> {
    loadScript();
    await waitForGrecaptcha(10_000);

    return new Promise((resolve, reject) => {
      window.grecaptcha!.ready(() => {
        window.grecaptcha!
          .execute(recaptchaV3SiteKey, {action})
          .then(resolve)
          .catch(reject);
      });
    });
  },
};
